import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/Kpi";
import { Link } from "react-router-dom";
import { Plus, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Leads() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ q: "", stage: "", source: "", project_id: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", mobile: "", email: "", project_id: "", source: "direct", configuration: "" });
  const [duplicate, setDuplicate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reassign, setReassign] = useState(null); // {lead, role_key, new_user_id, reason}
  const { user: me } = useAuth();
  const canReassign = ["super_admin","partner","management","business_head","site_head","sales_manager","gre"].includes(me?.role);

  const load = async () => {
    const p = { limit: 500 };
    if (filter.q) p.q = filter.q;
    if (filter.stage) p.stage = filter.stage;
    if (filter.source) p.source = filter.source;
    if (filter.project_id) p.project_id = filter.project_id;
    const { data } = await api.get("/leads", { params: p });
    setItems(data);
  };

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/users").then(r => setUsers(r.data));
  }, []);
  useEffect(() => { load(); }, [filter]);

  const submit = async () => {
    setBusy(true); setDuplicate(null);
    try {
      const { data } = await api.post("/leads", { ...form, force_create: !!duplicate });
      toast.success(`Lead ${data.lead_code} created`);
      setOpen(false); setForm({ client_name: "", mobile: "", email: "", project_id: "", source: "direct", configuration: "" });
      load();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.duplicate) {
        setDuplicate(e.response.data.detail);
        toast.warning("Duplicate detected — review before creating.");
      } else {
        toast.error(formatError(e.response?.data?.detail) || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Sales CRM</div>
          <h1 className="font-serif-display text-3xl">Leads</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lead-btn" className="bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={14} className="mr-1"/> New Lead</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Lead</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Client Name</Label>
                <Input data-testid="lead-name-input" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}/>
              </div>
              <div>
                <Label>Mobile</Label>
                <Input data-testid="lead-mobile-input" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}/>
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
              </div>
              <div>
                <Label>Project</Label>
                <Select value={form.project_id} onValueChange={v => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="lead-project-select"><SelectValue placeholder="Select project"/></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["direct", "meta", "google_search", "website", "walk_in", "channel_partner", "reference", "whatsapp"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Configuration</Label>
                <Input value={form.configuration} onChange={e => setForm({ ...form, configuration: e.target.value })} placeholder="e.g. 3 BHK"/>
              </div>
            </div>
            {duplicate && (
              <div className="border border-amber-500/40 bg-amber-500/10 rounded p-3 text-sm" data-testid="duplicate-warning">
                <div className="font-semibold text-amber-800">Existing client found: {duplicate.client.name} ({duplicate.client.client_code})</div>
                <div className="text-xs text-muted-foreground mt-1">{duplicate.existing_leads?.length || 0} existing lead(s) linked. You can proceed to add a new project interest for the same client.</div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); setDuplicate(null); }}>Cancel</Button>
              <Button disabled={busy} onClick={submit} data-testid="lead-submit-btn">{duplicate ? "Create Anyway" : "Create Lead"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input placeholder="Search name / mobile / code" value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} data-testid="leads-search-input"/>
          <Select value={filter.stage} onValueChange={v => setFilter({ ...filter, stage: v === "__all" ? "" : v })}>
            <SelectTrigger data-testid="leads-stage-filter"><SelectValue placeholder="Stage"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All stages</SelectItem>
              {["new","attempted","connected","qualified","visit_scheduled","visit_completed","negotiation","booked","lost"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.source} onValueChange={v => setFilter({ ...filter, source: v === "__all" ? "" : v })}>
            <SelectTrigger data-testid="leads-source-filter"><SelectValue placeholder="Source"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All sources</SelectItem>
              {["meta","google_search","website","walk_in","channel_partner","reference","whatsapp","direct"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.project_id} onValueChange={v => setFilter({ ...filter, project_id: v === "__all" ? "" : v })}>
            <SelectTrigger data-testid="leads-project-filter"><SelectValue placeholder="Project"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground flex items-center">Showing <span className="metric-value mx-2">{items.length}</span> leads</div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Lead Code</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Mobile</th>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Next Follow-up</th>
              {canReassign && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.map(l => {
              const proj = projects.find(p => p.id === l.project_id);
              return (
                <tr key={l.id} className="border-t border-border hover:bg-accent/5" data-testid={`lead-row-${l.lead_code}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/clients/${l.client_id}`} className="text-accent hover:underline">{l.lead_code}</Link>
                  </td>
                  <td className="px-4 py-3">{l.client_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.mobile}</td>
                  <td className="px-4 py-3">{proj?.name || "-"}</td>
                  <td className="px-4 py-3 capitalize text-xs">{(l.source || "").replace(/_/g," ")}</td>
                  <td className="px-4 py-3"><StatusBadge status={l.stage}/></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{l.next_followup_at ? new Date(l.next_followup_at).toLocaleDateString() : "-"}</td>
                  {canReassign && (
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setReassign({ lead: l, role_key: "assigned_closing_id", new_user_id: "", reason: "" })} data-testid={`reassign-${l.lead_code}`}><UserCog size={12} className="mr-1"/> Reassign</Button>
                    </td>
                  )}
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={canReassign ? 8 : 7} className="px-4 py-10 text-center text-muted-foreground">No leads match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!reassign} onOpenChange={(v) => !v && setReassign(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reassign Lead {reassign?.lead?.lead_code}</DialogTitle></DialogHeader>
          {reassign && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Ownership history is preserved and the new owner is notified in-app.</div>
              <div>
                <Label>Role to reassign</Label>
                <Select value={reassign.role_key} onValueChange={v => setReassign({ ...reassign, role_key: v, new_user_id: "" })}>
                  <SelectTrigger data-testid="reassign-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assigned_closing_id">Closing Manager</SelectItem>
                    <SelectItem value="assigned_sourcing_id">Sourcing Manager</SelectItem>
                    <SelectItem value="assigned_pre_sales_id">Pre-Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Owner</Label>
                <Select value={reassign.new_user_id} onValueChange={v => setReassign({ ...reassign, new_user_id: v })}>
                  <SelectTrigger data-testid="reassign-user"><SelectValue placeholder="Select employee"/></SelectTrigger>
                  <SelectContent className="max-h-64 overflow-auto">
                    {users.filter(u => u.active && (
                      reassign.role_key === "assigned_closing_id" ? u.role === "closing_manager" :
                      reassign.role_key === "assigned_sourcing_id" ? u.role === "sourcing_manager" :
                      u.role === "pre_sales"
                    )).map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.division}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reason (audit-logged)</Label><Input value={reassign.reason} onChange={e => setReassign({ ...reassign, reason: e.target.value })}/></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={async () => {
              try {
                await api.post("/leads/reassign", { lead_id: reassign.lead.id, role_key: reassign.role_key, new_user_id: reassign.new_user_id, reason: reassign.reason });
                toast.success("Reassigned"); setReassign(null); load();
              } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
            }} data-testid="reassign-submit">Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
